import { useState } from "react";
import { ChevronDown, ChevronRight, Smartphone } from "lucide-react";
import { useFetch } from "../lib/api";
import { useMember } from "../lib/member";
import { getVisit } from "../lib/offline";
import { PageHeader, Empty, Pill } from "../components/shared";

const when = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const span = (a, b) => { const m = Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000)); return m < 1 ? "under 1 min" : m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`; };
const KIND = { doc: "Documents", worker: "New workers", attendance: "Attendance" };

const Counts = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">{Object.entries(items.reduce((a, i) => ({ ...a, [i.kind]: (a[i.kind] || 0) + 1 }), {})).map(([k, n]) => <Pill key={k} tone="blue">{n} {KIND[k] || k}</Pill>)}</div>);

const Visit = ({ v, admin }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1F2A3E]/60 last:border-0" data-testid={`visit-row-${v.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full grid sm:grid-cols-[1.3fr_1fr_1fr_auto] gap-2 items-center text-left px-4 py-3 hover:bg-[#1A2234]/50" data-testid={`visit-toggle-${v.id}`}>
        <span className="flex items-center gap-2 text-sm">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span><span className="font-medium">{when(v.started_at)}</span>{admin && <span className="block text-[11px] text-slate-400">{v.user_name || v.user_email}</span>}</span></span>
        <span className="text-xs text-slate-400">No signal for {span(v.started_at, v.back_online_at)}<span className="block">Uploaded {when(v.uploaded_at)}</span></span>
        <Counts items={v.items} />
        {v.failed > 0 ? <Pill tone="red">{v.failed} not uploaded</Pill> : <Pill tone="green">All uploaded</Pill>}
      </button>
      {open && <ul className="px-10 pb-3 grid gap-1" data-testid={`visit-items-${v.id}`}>
        {v.items.map((i, n) => <li key={n} className="text-xs text-slate-300 flex justify-between gap-3"><span>{i.label}</span><span className="text-slate-500 font-mono shrink-0">{when(i.saved_at)}</span></li>)}</ul>}
    </div>);
};

export default function SiteVisits() {
  const { me } = useMember();
  const { data, error } = useFetch("/site-visits");
  const admin = me?.role === "admin";
  const here = getVisit();
  return (
    <div>
      <PageHeader eyebrow="Field work" title="Site Visits" desc={`Each time a phone loses signal, what staff capture is logged here once it uploads.${admin ? " You see everyone's visits." : " You see your own visits."}`} />
      {here && <div className="c-surface c-rise mb-4 p-4 flex flex-wrap items-center gap-3 !border-amber-400/30" data-testid="visit-in-progress">
        <Smartphone size={16} className="text-amber-300" /><span className="text-sm">Visit in progress on this phone since {when(here.started_at)}: {here.items.length} item(s) saved, waiting to upload</span></div>}
      <div className="c-surface c-rise" data-testid="visits-list">
        {error ? <Empty text={error} /> : !data ? <Empty text="Loading…" /> : data.length ? data.map((v) => <Visit key={v.id} v={v} admin={admin} />)
          : <Empty text="No site visits yet. They appear after staff capture work with no signal." testid="visits-empty" />}
      </div>
    </div>);
}
